import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useCreateUserMutation } from '../../../pages/authentication/data-access/+state/user.api';
import { buildCreateAccountPayload } from '@shared/types/user.types';
import { setAccountDomain } from '@app/shared/utils/accountDomain.utils';
import { useGetDomainsQuery } from '../../../app/api/domains.api';
import DomainSelect from 'components/forms/DomainSelect';
import { useFormik } from 'formik';
import {
  signUpValidationSchema,
  filterNameInput,
  filterEmailInput,
  filterOrganizationNameInput
} from '../../../utils/validationUtils';

const SignUpForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const [createUser, { isLoading, isSuccess }] = useCreateUserMutation();
  const { data: domainsData } = useGetDomainsQuery();

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [serverError, setServerError] = useState('');

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      username: '',
      organizationName: '',
      password: '',
      confirmPassword: '',
      domain: '',
      acceptedTerms: false
    },
    validationSchema: signUpValidationSchema,
    onSubmit: async values => {
      setServerError('');
      try {
        const signupDomain = values.domain.trim().toLowerCase();
        const response = await createUser(
          buildCreateAccountPayload({
            username: values.username,
            password: values.password,
            organizationName: values.organizationName,
            firstName: values.firstName,
            lastName: values.lastName,
            domain: signupDomain
          })
        ).unwrap();

        // Store organizationId from response
        if (response?.organizationId) {
          localStorage.setItem('organizationId', response.organizationId);
        }
        setAccountDomain(signupDomain);
      } catch (error: any) {
        let errorMessage = 'Something went wrong. Please try again.';

        if (error?.data?.message) {
          const serverMessage = error.data.message.toLowerCase();

          // Check if it's a duplicate email/username error
          if (
            serverMessage.includes('username') ||
            serverMessage.includes('email') ||
            serverMessage.includes('already exists') ||
            serverMessage.includes('already exits') ||
            serverMessage.includes('duplicate')
          ) {
            // Set error in Formik
            formik.setFieldError(
              'username',
              'This email address is already registered. Please use a different email or sign in.'
            );
            errorMessage = 'This email address is already registered. Please use a different email or sign in.';
          } else {
            errorMessage = error.data.message;
          }
        }

        setServerError(errorMessage);
        setShowErrorSnackbar(true);
      }
    }
  });

  useEffect(() => {
    if (isSuccess) {
      setShowSuccessDialog(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    const domains = domainsData?.domains;
    if (!domains?.length || formik.values.domain) return;
    formik.setFieldValue('domain', domains[0].name);
    // Only set the default once domains are loaded.
  }, [domainsData, formik]);

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/');
  };

  // Handler to filter name input in real-time
  const handleNameChange = (value: string, fieldName: 'firstName' | 'lastName') => {
    const filteredValue = filterNameInput(value);
    formik.setFieldValue(fieldName, filteredValue);
  };

  // Handler to filter email input in real-time
  const handleEmailChange = (value: string) => {
    const filteredValue = filterEmailInput(value);
    formik.setFieldValue('username', filteredValue);
  };

  // Handler to filter organization name input in real-time
  const handleOrganizationNameChange = (value: string) => {
    const filteredValue = filterOrganizationNameInput(value);
    formik.setFieldValue('organizationName', filteredValue);
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign Up</h3>
        <p className="text-body-tertiary">Create your account today</p>
      </div>

      <div className="position-relative mt-4">
        <hr className="bg-body-secondary" />
        <div className="divider-content-center">or use email</div>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="firstName">First Name</Form.Label>
              <Form.Control
                id="firstName"
                name="firstName"
                type="text"
                value={formik.values.firstName}
                onChange={e => handleNameChange(e.target.value, 'firstName')}
                onBlur={formik.handleBlur}
                placeholder="John"
                isInvalid={!!(formik.touched.firstName && formik.errors.firstName)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.touched.firstName && formik.errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="lastName">Last Name</Form.Label>
              <Form.Control
                id="lastName"
                name="lastName"
                type="text"
                value={formik.values.lastName}
                onChange={e => handleNameChange(e.target.value, 'lastName')}
                onBlur={formik.handleBlur}
                placeholder="Doe"
                isInvalid={!!(formik.touched.lastName && formik.errors.lastName)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.touched.lastName && formik.errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Email address</Form.Label>
          <Form.Control
            id="email"
            name="username"
            type="email"
            value={formik.values.username}
            onChange={e => handleEmailChange(e.target.value)}
            onBlur={formik.handleBlur}
            placeholder="name@example.com"
            isInvalid={!!(formik.touched.username && formik.errors.username)}
          />
          <Form.Control.Feedback type="invalid">
            {formik.touched.username && formik.errors.username}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="organizationName">Organization Name</Form.Label>
          <Form.Control
            id="organizationName"
            name="organizationName"
            type="text"
            value={formik.values.organizationName}
            onChange={e => handleOrganizationNameChange(e.target.value)}
            onBlur={formik.handleBlur}
            placeholder="Acme Corp"
            isInvalid={!!(formik.touched.organizationName && formik.errors.organizationName)}
          />
          <Form.Control.Feedback type="invalid">
            {formik.touched.organizationName && formik.errors.organizationName}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="domain">Domain</Form.Label>
          <DomainSelect
            id="domain"
            name="domain"
            value={formik.values.domain}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={!!(formik.touched.domain && formik.errors.domain)}
            invalidFeedback={formik.touched.domain && formik.errors.domain}
          />
        </Form.Group>

        <Row className="g-3 mb-3">
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="password">Password</Form.Label>
              <Form.Control
                id="password"
                name="password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Password (8-64 characters)"
                isInvalid={!!(formik.touched.password && formik.errors.password)}
                maxLength={64}
              />
              <Form.Control.Feedback type="invalid">
                {formik.touched.password && formik.errors.password}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="confirmPassword">Confirm Password</Form.Label>
              <Form.Control
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Confirm Password"
                isInvalid={!!(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                maxLength={64}
              />
              <Form.Control.Feedback type="invalid">
                {formik.touched.confirmPassword && formik.errors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Check type="checkbox" className="mb-3">
          <Form.Check.Input
            type="checkbox"
            name="acceptedTerms"
            id="termsService"
            checked={formik.values.acceptedTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={!!(formik.touched.acceptedTerms && formik.errors.acceptedTerms)}
          />
          <Form.Check.Label htmlFor="termsService" className="fs-9 text-transform-none">
            I accept the <Link to="#!">terms</Link> and <Link to="#!">privacy policy</Link>
          </Form.Check.Label>
          {formik.touched.acceptedTerms && formik.errors.acceptedTerms && (
            <div className="text-danger small mt-1">{formik.errors.acceptedTerms}</div>
          )}
        </Form.Check>

        {serverError && <div className="text-danger text-center mb-3">{serverError}</div>}

        <Button type="submit" variant="primary" className="w-100 mb-3" disabled={isLoading || formik.isSubmitting}>
          {isLoading || formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Sign Up'}
        </Button>

        <div className="text-center">
          <Link to={`/`} className="fs-9 fw-bold">
            Sign in to an existing account
          </Link>
        </div>
      </Form>

      {/* ✅ Success Modal */}
      <Dialog open={showSuccessDialog} onClose={handleDialogClose}>
        <DialogTitle>Account Created</DialogTitle>
        <DialogContent>
          <DialogContentText>Your account has been created successfully.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleDialogClose} autoFocus>
            Okay
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* ❌ Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" variant="filled">
          {serverError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUpForm;
